// JS twin of the SQL backfill in
// supabase/migrations/<ts>_briefing_crew_backfill.sql.
//
// Both implementations apply the same grouping rule so re-running the
// SQL backfill produces a row set equivalent to what this returns.
//
// crew_members today carries one row per (briefing, person, event_date).
// The producer flow keeps a person's id stable across their day rows
// (CreateEditBriefing.tsx:833 "Keep the same ID so assignments stay
// linked"), but that convention isn't enforced in the schema. The
// canonical key for "is this the same person" is therefore the
// case-insensitive trimmed email — same rule used in the existing
// existingCrewByKey lookup at CreateEditBriefing.tsx:1066.

export type LegacyCrewRow = {
  name: string;
  email: string;
  phone: string | null;
  color: string | null;
  user_id: string | null;
  event_date: string; // ISO yyyy-mm-dd
  function: string;
  arrival_time: string | null; // ISO time HH:mm:ss
  camera_id?: string | null; // 1–3 letter Camera ID, per (person, day)
};

export type BriefingCrewRecord = {
  briefing_id: string;
  name: string;
  email: string; // normalized: lowercase + trimmed
  phone: string | null;
  color: string | null;
  user_id: string | null;
};

export type BriefingCrewDayRecord = {
  briefing_id: string;
  canonicalKey: string; // briefing_id + ":" + normalized email — for linking back to BriefingCrewRecord
  event_date: string;
  function: string;
  arrival_time: string | null;
  camera_id: string | null; // 1–3 letter Camera ID, per (person, day)
};

const normalizeEmail = (e: string | null | undefined): string => (e ?? "").trim().toLowerCase();

const preferNonNull = <T>(existing: T | null, incoming: T | null): T | null => incoming ?? existing;

export function legacyCrewToRecords(input: { briefingId: string; legacyRows: LegacyCrewRow[] }): {
  crew: BriefingCrewRecord[];
  days: BriefingCrewDayRecord[];
} {
  const { briefingId, legacyRows } = input;
  if (legacyRows.length === 0) return { crew: [], days: [] };

  type Acc = {
    name: string;
    email: string;
    phone: string | null;
    color: string | null;
    user_id: string | null;
    firstSeenPosition: number;
  };
  const byEmail = new Map<string, Acc>();
  const days: BriefingCrewDayRecord[] = [];

  legacyRows.forEach((row, position) => {
    const email = normalizeEmail(row.email);
    if (!email) return; // can't group without an email

    let acc = byEmail.get(email);
    if (!acc) {
      acc = {
        name: row.name,
        email,
        phone: row.phone,
        color: row.color,
        user_id: row.user_id,
        firstSeenPosition: position,
      };
      byEmail.set(email, acc);
    } else {
      // Defensive merge: prefer non-null fields from later rows so a
      // null on the first row doesn't permanently win.
      acc.phone = preferNonNull(acc.phone, row.phone);
      acc.color = preferNonNull(acc.color, row.color);
      acc.user_id = preferNonNull(acc.user_id, row.user_id);
    }

    days.push({
      briefing_id: briefingId,
      canonicalKey: `${briefingId}:${email}`,
      event_date: row.event_date,
      function: row.function,
      arrival_time: row.arrival_time,
      // Camera ID is a per-(person, day) field, so it rides on the day
      // record (like function / arrival_time), not the per-person crew
      // record. Empty string → null so an unassigned slot stays clean.
      camera_id: row.camera_id && row.camera_id.trim() !== "" ? row.camera_id.trim() : null,
    });
  });

  const crew = Array.from(byEmail.values())
    .sort((a, b) => a.firstSeenPosition - b.firstSeenPosition)
    .map((acc) => ({
      briefing_id: briefingId,
      name: acc.name,
      email: acc.email,
      phone: acc.phone,
      color: acc.color,
      user_id: acc.user_id,
    }));

  return { crew, days };
}
